import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { format } from 'date-fns';
import { CalendarIcon, CreditCard } from 'lucide-react';
import { cn } from '@/lib/utils';
import { usePaymentRecording } from './usePaymentRecording';
import OverpaymentConfirmModal from './OverpaymentConfirmModal';

interface PaymentRecordModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  invoice: {
    id: string;
    invoice_number: string;
    total_amount: number;
    paid_amount: number;
    credit_applied: number;
    customer_id: string;
  };
}

const PAYMENT_METHODS = [
  { value: 'EFT', label: 'EFT (Bank Transfer)' },
  { value: 'Cash', label: 'Cash' },
  { value: 'Card', label: 'Card' },
  { value: 'Cheque', label: 'Cheque' },
];

const PaymentRecordModal: React.FC<PaymentRecordModalProps> = ({
  open,
  onOpenChange,
  invoice,
}) => {
  const creditApplied = invoice.credit_applied || 0;
  const netAmount = (invoice.total_amount || 0) - creditApplied;
  const remainingBalance = netAmount - (invoice.paid_amount || 0);
  
  const [paymentDate, setPaymentDate] = useState<Date>(new Date());
  const [paymentAmount, setPaymentAmount] = useState<string>(remainingBalance.toFixed(2));
  const [paymentMethod, setPaymentMethod] = useState<string>('EFT');
  const [notes, setNotes] = useState<string>('');
  const [showOverpaymentModal, setShowOverpaymentModal] = useState(false);
  const [pendingOverpayment, setPendingOverpayment] = useState<number>(0);

  const { recordPayment, isRecording, createCreditFromOverpayment, isCreatingCredit } = usePaymentRecording(invoice.id);

  // Reset form when modal opens
  useEffect(() => {
    if (open) {
      setPaymentDate(new Date());
      setPaymentAmount(remainingBalance.toFixed(2));
      setPaymentMethod('EFT');
      setNotes('');
    }
  }, [open, remainingBalance]);

  const handleSubmit = async () => {
    const amount = parseFloat(paymentAmount);
    if (isNaN(amount) || amount <= 0) {
      return;
    }

    // Check for overpayment
    if (amount > remainingBalance) {
      setPendingOverpayment(amount - remainingBalance);
      setShowOverpaymentModal(true);
      return;
    }

    try {
      await recordPayment({
        invoiceId: invoice.id,
        paymentDate,
        paymentAmount: amount,
        paymentMethod,
        notes: notes || undefined,
      });
      onOpenChange(false);
    } catch (error) {
      // Error handled in hook
    }
  };

  const handleOverpaymentConfirm = async (createCredit: boolean) => {
    const amount = parseFloat(paymentAmount);
    
    try {
      // Record the FULL payment amount (actual cash received)
      // The usePaymentRecording hook will cap invoice.paid_amount at total
      await recordPayment({
        invoiceId: invoice.id,
        paymentDate,
        paymentAmount: amount, // Record actual amount received
        paymentMethod,
        notes: notes || undefined,
      });

      // Create credit if requested
      if (createCredit && invoice.customer_id) {
        await createCreditFromOverpayment({
          customerId: invoice.customer_id,
          overpaymentAmount: pendingOverpayment,
          invoiceId: invoice.id,
        });
      }

      setShowOverpaymentModal(false);
      onOpenChange(false);
    } catch (error) {
      // Error handled in hooks
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CreditCard className="h-5 w-5" />
              Record Payment
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Invoice Info */}
            <div className="bg-muted/50 rounded-lg p-3 space-y-1 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Invoice:</span>
                <span className="font-medium">{invoice.invoice_number}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Total Amount:</span>
                <span>R {(invoice.total_amount || 0).toFixed(2)}</span>
              </div>
              {creditApplied > 0 && (
                <div className="flex justify-between text-green-600">
                  <span>Credits Applied:</span>
                  <span>- R {creditApplied.toFixed(2)}</span>
                </div>
              )}
              <div className="flex justify-between">
                <span className="text-muted-foreground">Net Amount Due:</span>
                <span>R {netAmount.toFixed(2)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Already Paid:</span>
                <span>R {(invoice.paid_amount || 0).toFixed(2)}</span>
              </div>
              <div className="flex justify-between font-semibold border-t pt-1 mt-1">
                <span>Remaining:</span>
                <span>R {remainingBalance.toFixed(2)}</span>
              </div>
            </div>

            {/* Payment Date */}
            <div className="space-y-2">
              <Label>Payment Date</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      'w-full justify-start text-left font-normal',
                      !paymentDate && 'text-muted-foreground'
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {paymentDate ? format(paymentDate, 'dd MMMM yyyy') : 'Pick a date'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={paymentDate}
                    onSelect={(date) => date && setPaymentDate(date)}
                    initialFocus
                    className="pointer-events-auto"
                  />
                </PopoverContent>
              </Popover>
            </div>

            {/* Payment Amount */}
            <div className="space-y-2">
              <Label>Amount</Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                  R
                </span>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={paymentAmount}
                  onChange={(e) => setPaymentAmount(e.target.value)}
                  className="pl-7"
                />
              </div>
            </div>

            {/* Payment Method */}
            <div className="space-y-2">
              <Label>Payment Method</Label>
              <Select value={paymentMethod} onValueChange={setPaymentMethod}>
                <SelectTrigger>
                  <SelectValue placeholder="Select method" />
                </SelectTrigger>
                <SelectContent>
                  {PAYMENT_METHODS.map((method) => (
                    <SelectItem key={method.value} value={method.value}>
                      {method.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Notes */}
            <div className="space-y-2">
              <Label>Notes (Optional)</Label>
              <Textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Payment reference, details..."
                rows={2}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button onClick={handleSubmit} disabled={isRecording}>
              {isRecording ? 'Recording...' : 'Record Payment'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <OverpaymentConfirmModal
        open={showOverpaymentModal}
        onOpenChange={setShowOverpaymentModal}
        overpaymentAmount={pendingOverpayment}
        onConfirm={handleOverpaymentConfirm}
        isLoading={isRecording || isCreatingCredit}
      />
    </>
  );
};

export default PaymentRecordModal;
